import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Paper, ClickAwayListener, TextField, InputAdornment, Portal } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@emotion/react';
import { tokens } from '../theme';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';

const StyledPortal = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: 0,
  width: '100%',
  maxHeight: 280,
  minHeight: 50,
  overflowY: 'auto',
  zIndex: 9999,
  p: theme.spacing(1),
  borderRadius: theme.spacing(1),
  boxShadow: '0px 5px 15px rgba(0,0,0,0.2)',
  backgroundColor: theme.palette.background.paper
}));

const ColoredSelector = ({
  options = [],
  value = null,
  onChange,
  placeholder = "Select an option",
  label = "Select",
  colorKey = "color",
  valueKey = "id",
  labelKey = "name",
  searchable = true,
  error = false,
  helperText = "",
  required = false,
  disabled = false,
  showColorDot = true
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const containerRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Find the selected option object based on value (tolerate type differences)
  const selectedOption = options.find(opt => String(opt[valueKey]) === String(value));

  // Filter options based on search text
  const filteredOptions = searchText.trim() === '' 
    ? options
    : options.filter(opt => 
        opt[labelKey].toLowerCase().includes(searchText.toLowerCase())
      );

  // Handle dropdown toggle
  const handleToggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      // Set the anchor element for the Portal
      if (!isOpen && containerRef.current) {
        setAnchorEl(containerRef.current);
      } else {
        setAnchorEl(null);
      }
      setSearchText('');
    }
  };

  // Handle option selection
  const handleSelectOption = (option) => {
    onChange(option[valueKey]);
    setIsOpen(false);
    setSearchText('');
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  // Close dropdown when clicking outside
  const handleClickAway = () => {
    setIsOpen(false);
    setSearchText('');
    setAnchorEl(null);
  };

  // Simple effect to handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box 
        ref={containerRef}
        sx={{ 
          position: 'relative',
          width: '100%',
          mb: helperText ? 2 : 0
        }}
      >
        {/* Label */}
        {label && (
          <Typography 
            variant="caption" 
            sx={{ 
              mb: 0.5, 
              color: error ? 'error.main' : colors.grey[600],
              display: 'block'
            }}
          >
            {label}{required && <span style={{ color: colors.grey[600] }}> *</span>}
          </Typography>
        )}
        
        {/* Selected value display / Dropdown trigger */}
        <Box
          onClick={handleToggleDropdown}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: `1px solid ${error ? theme.palette.error.main : colors.grey[300]}`,
            borderRadius: '8px',
            p: 1.5,
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: disabled ? colors.grey[100] : 'background.paper',
            opacity: disabled ? 0.7 : 1,
            '&:hover': {
              borderColor: !disabled && (error ? theme.palette.error.light : colors.primary[400])
            },
            transition: 'all 0.2s',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedOption ? (
              <>
                {showColorDot && (
                  <Box 
                    sx={{ 
                      width: 20, 
                      height: 20, 
                      borderRadius: '50%',
                      backgroundColor: selectedOption[colorKey] || colors.primary[400],
                    }} 
                  />
                )}
                <Typography sx={{ color: colors.grey[800] }}>
                  {selectedOption[labelKey]}
                </Typography>
              </>
            ) : (
              <Typography sx={{ color: colors.grey[500] }}>
                {placeholder}
              </Typography>
            )}
          </Box>
          <KeyboardArrowDownIcon 
            sx={{ 
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 0.2s ease-in-out',
              color: colors.grey[500]
            }} 
          />
        </Box>

        {/* Helper text / Error message */}
        {helperText && (
          <Typography 
            variant="caption" 
            sx={{ 
              mt: 0.5, 
              color: error ? 'error.main' : colors.grey[500],
              display: 'block'
            }}
          >
            {helperText}
          </Typography>
        )}

        {/* Dropdown using Portal to render outside containing divs */}
        {isOpen && anchorEl && (
          <Portal>
            <Paper
              elevation={5}
              sx={{
                position: 'absolute',
                top: anchorEl.getBoundingClientRect().bottom + 4,
                left: anchorEl.getBoundingClientRect().left,
                width: anchorEl.offsetWidth,
                maxHeight: 280,
                overflowY: 'auto',
                zIndex: 9999,
                p: 1,
                borderRadius: '8px',
                boxShadow: '0px 5px 15px rgba(0,0,0,0.2)',
                backgroundColor: theme.palette.mode === 'dark' ? colors.primary[500] : '#fff'
              }}
            >
              {/* Search input */}
              {searchable && options.length > 5 && (
                <Box sx={{ p: 1 }}>
                  <TextField
                    autoFocus
                    size="small"
                    fullWidth
                    placeholder="Search..."
                    value={searchText}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                      }
                    }}
                  />
                </Box>
              )}

            {/* Options list */}
            <Box sx={{ mt: searchable ? 1 : 0 }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, idx) => {
                  const isSelected = String(value) === String(option[valueKey]);
                  const optionKey = String(
                    option[valueKey] ?? option.id ?? option[labelKey] ?? option.title ?? option.name ?? ''
                  );
                  return (
                    <Box
                      key={optionKey && optionKey !== 'NaN' ? optionKey : `opt-${idx}`}
                      onClick={() => handleSelectOption(option)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        cursor: 'pointer',
                        borderRadius: 1,
                        backgroundColor: isSelected ? colors.grey[100] : 'transparent',
                        '&:hover': {
                          backgroundColor: colors.grey[100],
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {showColorDot && (
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: option[colorKey] || colors.primary[400],
                            }}
                          />
                        )}
                        <Typography sx={{ color: colors.grey[800] }}>
                          {option[labelKey]}
                        </Typography>
                      </Box>
                      {isSelected && (
                        <CheckIcon 
                          fontSize="small" 
                          sx={{ color: colors.primary[500] }} 
                        />
                      )}
                    </Box>
                  );
                })
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No options found
                  </Typography>
                </Box>
              )}
            </Box>
            </Paper>
          </Portal>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default ColoredSelector;
